import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import dynamic from 'next/dynamic';
// Api
import { TRACK_SHARE } from 'api/store/mutations';
// Types
import { GetMediaPost_getMediaPost_ImagePost } from 'api/mediaPost/types/GetMediaPost';
import { TrackShare, TrackShareVariables } from 'api/store/types/TrackShare';
import {
  TrackedShareTarget,
  TrackedSourceType,
} from 'api/graphql-global-types';
// Helpers
import { getEnvLink } from 'helpers/routes';
import { parseForInnerHTML } from 'helpers/textEditor';
import { formatVideoDetailsDate } from 'helpers/dateText';
// Hooks
import { useBreakpoints, useGetCurrUser } from 'hooks';
import { useTrackViews, AnalyticsTargetType } from 'hooks/analytics';
// Constants
import { SOCIAL_MEDIA_POST } from 'constants/routes';
import { BLACK_MODAL_BACKGROUND_STYLE } from 'constants/colors';
// Components
import { showToast } from 'components/common/Toast/Toast';
import MentionsView from 'components/MentionsView/MentionsView';
// Ui
import Image from 'ui3/Image/Image';
import HashTags from 'ui3/HashTags/HashTags';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Styles
import styles from './MediaFeedItemImage.module.scss';
// Dynamic imports
const MediaFeedItemButtons = dynamic(
  import('../MediaFeedItemButtons/MediaFeedItemButtons'),
  {
    ssr: false,
  }
);
const UploadMediaChannel = dynamic(
  import(
    '../../../../ProfileSetup/components/ChannelCreation/ChannelCard/UploadMediaChannel/UploadMediaChannel'
  ),
  {
    ssr: false,
  }
);

type MediaFeedItemImageProps = {
  mediaFeed: GetMediaPost_getMediaPost_ImagePost;
};

const MediaFeedItemImage = ({
  mediaFeed,
}: MediaFeedItemImageProps): JSX.Element | null => {
  const [trackShare] = useMutation<TrackShare, TrackShareVariables>(
    TRACK_SHARE
  );
  const { isBreakpointL } = useBreakpoints();

  const { data } = useGetCurrUser();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Analytics hooks - Views Algorithm (V_A direct view, V_E share)
  const { trackDirectView, trackMessageShare } = useTrackViews({
    targetType: AnalyticsTargetType.MEDIA_POST,
  });

  const { store, image, title, slug, body } = mediaFeed;

  const [
    mediaPostModalVisibility,
    setMediaPostModalVisibility,
  ] = useState<boolean>(false);

  const handleMediaPostModalOpen = () => {
    setMediaPostModalVisibility(true);
  };

  const handleMediaPostModalClose = () => {
    setMediaPostModalVisibility(false);
  };

  const isOwner = data?.me.id === store.id;

  const storeSlug = store.slug;

  const link = `${getEnvLink()}/${storeSlug}${SOCIAL_MEDIA_POST}${slug}`;

  const handleToggleDescription = () => {
    setIsExpanded(!isExpanded);
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(link);

      await trackMessageShare(mediaFeed.id);

      // Also track share for backwards compatibility
      await trackShare({
        variables: {
          input: {
            shareTarget: TrackedShareTarget.Copy,
            sourceId: mediaFeed.id,
            sourceType: TrackedSourceType.MediaPost,
          },
        },
      });

      showToast({
        message: 'Copied',
        autoClose: 1000,
      });
    } catch (error) {
      showToast({
        message: (error as Error).message || 'Could not copy text',
        type: 'error',
        autoClose: 1000,
      });
    }
  };

  const [viewRecorded, setViewRecorded] = useState(false);

  const handleMediaPostViewed = useCallback(
    async (postId: string) => {
      try {
        await trackDirectView(postId);
        setViewRecorded(true);
      } catch (error) {
        console.error(error);
      }
    },
    [trackDirectView]
  );

  useEffect(() => {
    if (!viewRecorded) {
      handleMediaPostViewed(mediaFeed.id);
    }
  }, [viewRecorded, handleMediaPostViewed, mediaFeed]);

  return (
    <div className={styles.root} ref={rootRef}>
      <div className={styles.imageContainer}>
        <Image
          className={styles.image}
          src={image.url}
          alt=""
          sizes="(min-width: 1024px) 600px, (max-width: 1024px) 360px"
        />
      </div>
      <div className={styles.bodyTextWrapper}>
        <h1 title={title} className={styles.title}>
          {title}
        </h1>

        <MediaFeedItemButtons
          store={store}
          isOwner={isOwner}
          mediaFeed={mediaFeed}
          handleMediaPostModalOpen={handleMediaPostModalOpen}
          handleShare={handleShare}
          className={styles.actionButtons}
        />

        <div className={styles.descriptionSection}>
          <div className={styles.videoDetailsRow}>
            <div className={styles.viewsDateSection}>
              <span className={styles.viewsText}>
                <MillionsIcon name="eye" className={styles.eyeIcon} />
                {mediaFeed.views?.viewCount || 0} views
              </span>
              <span className={styles.dateText}>
                {formatVideoDetailsDate(
                  mediaFeed?.scheduledAt ?? mediaFeed.createdAt
                )}
              </span>
            </div>
          </div>

          {body && (
            <div className={styles.videoDetailsRow}>
              <div
                className={styles.descriptionText}
                dangerouslySetInnerHTML={{
                  __html: parseForInnerHTML(body),
                }}
              />
            </div>
          )}

          {isExpanded && (
            <>
              {mediaFeed.mentions && mediaFeed.mentions.length > 0 && (
                <div className={styles.mentionsSection}>
                  <MentionsView
                    mentions={mediaFeed.mentions as any}
                    size="sm"
                  />
                </div>
              )}

              {mediaFeed.hashtags && mediaFeed.hashtags.length > 0 && (
                <>
                  <hr className={styles.dividerLine} />
                  <p className={styles.hashtagsLabel}>TAGS</p>
                  <div className={styles.hashtagsSection}>
                    <HashTags
                      tags={mediaFeed.hashtags}
                      className={styles.inlineTags}
                      hashTagClassName={styles.smallHashTag}
                    />
                  </div>
                </>
              )}
            </>
          )}

          <div className={styles.actionsRow}>
            <button
              className={styles.toggleButton}
              onClick={handleToggleDescription}
              type="button"
            >
              {isExpanded ? 'Show Less' : 'Show More'}
            </button>
          </div>
        </div>
      </div>
      {isOwner && (
        <UploadMediaChannel
          mediaFeed={mediaFeed}
          oldSlug={slug}
          modalProps={{
            open: mediaPostModalVisibility,
            onClose: handleMediaPostModalClose,
            title: 'Edit Media Post',
            fullPageOnMobile: true,
            blockScroll: isBreakpointL,
            modalStyles: BLACK_MODAL_BACKGROUND_STYLE,
          }}
        />
      )}
    </div>
  );
};
export default MediaFeedItemImage;
